// W23 Felhasználólista · W24 Új/szerkesztett felhasználó · W25 Saját profil
// · W26 Csoportok · W27 Munkakörök · W28 Felhasználóválasztó

import { t } from '../lib/i18n';
import { avatar, emptyState } from '../lib/components';
import { openDialog, confirmDialog } from '../lib/dialog';
import { h, icon, icons } from '../lib/dom';
import { selectField, textField, validateAll, type FieldControl } from '../lib/fields';
import type { PersonView } from '../lib/data';

export type Role = 'SUPER' | 'ADMIN' | 'USER';

/// A tervek szóhasználata: „Munkatárs" és „Cégadmin" — ezek mondják meg, mit
/// jelent a szerepkör a cégen belül, az „Admin" / „Felhasználó" nem.
const roleLabelKeys: Record<Role, string> = {
  SUPER: 'people.roleSuper',
  ADMIN: 'people.roleAdmin',
  USER: 'people.roleUser',
};

/// FÜGGVÉNY, nem konstans: kész szöveget tároló modul szintű táblaként a
/// fájl betöltésekor állna össze, a szótár viszont csak a render előtt
/// érkezik meg — a feliratok a nyers kulcsokkal fagynának be. A kulcsok
/// szándékosan idézőjeles literálok: így a `verify:i18n` is látja őket.
export function roleLabel(role: Role): string {
  return t(roleLabelKeys[role]);
}

export type GroupView = { id: string; name: string; userIds: string[] };

/// Kis- és nagybetűtől, valamint ékezettől független keresés.
///
/// A magyar neveknél a felhasználó ritkán gépel ékezetet a keresőbe; enélkül
/// a "toth" nem találná meg a "Tóth"-ot.
export function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function searchPeople(people: PersonView[], query: string): PersonView[] {
  const needle = normalize(query.trim());
  if (needle.length === 0) return people;
  return people.filter(
    (person) =>
      normalize(person.fullName).includes(needle) ||
      normalize(person.jobTitle ?? '').includes(needle),
  );
}

// ---------------------------------------------------------------------------
// W23 — Felhasználólista
// ---------------------------------------------------------------------------

export function peoplePage(options: {
  people: PersonView[];
  roles: Record<string, Role>;
  groups: GroupView[];
  jobTitleCount?: number;
  /// A cég férőhelykerete (`maxUser` a cégdokumentumon). Hiánya esetén a
  /// kihasználtság-sáv elmarad.
  seatLimit?: number;
  onNewUser: () => void;
  onEditUser: (person: PersonView) => void;
  onManageGroups: () => void;
  onManageJobTitles: () => void;
}): HTMLElement {
  let query = '';
  const listHost = h('div');

  function row(person: PersonView): HTMLElement {
    return h(
      'div',
      {
        class: 'lrow',
        style: { alignItems: 'center', flexWrap: 'nowrap' },
      },
      h('span', { style: { width: '44px', flex: 'none' } }, avatar(person.fullName, 'md', person.photoUrl)),
      h(
        'span',
        { class: 'col grow', style: { gap: '0', minWidth: '0' } },
        h('span', { class: 'body-sm w6', text: person.fullName }),
        h('span', { class: 'caption', text: person.jobTitle ?? t('people.noJobTitle') }),
      ),
      h('span', { style: { width: '130px', flex: 'none' } },
        h('span', {
          class: 'chip',
          style: { height: '26px' },
          text: roleLabel(options.roles[person.id] ?? 'USER'),
        }),
      ),
      // A terv „Allapot" oszlopa („Meghivo fuggoben", „Inaktiv") ide kerulne.
      // A felhasznalo-dokumentumon ilyen mezo NINCS (se meghivas-allapot, se
      // aktivitas), ezert az oszlop helyen az all, ami tudhato: van-e mar
      // profilkepe, azaz berendezkedett-e a fiokban.
      h(
        'span',
        { style: { width: '150px', flex: 'none' } },
        person.photoUrl
          ? h('span', { class: 'caption tm', text: '' })
          : h('span', {
              class: 'badge st-todo',
              style: { height: '22px', fontSize: '11px' },
              text: t('people.noPhoto'),
            }),
      ),
      h(
        'button',
        {
          class: 'btn btn-ghost btn-icon btn-sm',
          type: 'button',
          'aria-label': `${person.fullName} szerkesztése`,
          style: { flex: 'none' },
          onClick: () => options.onEditUser(person),
        },
        h('span', { class: 'ic' }, icon(icons.pencil)),
      ),
    );
  }

  function renderList(): void {
    const filtered = searchPeople(options.people, query);
    const limit = options.seatLimit;
    const free = limit === undefined ? undefined : Math.max(0, limit - options.people.length);

    listHost.replaceChildren(
      filtered.length === 0
        ? emptyState({ title: t('people.noMatch') })
        : h(
            'div',
            { class: 'panel', style: { overflow: 'hidden' } },
            h(
              'div',
              { class: 'thead' },
              h('span', { class: 'th', style: { width: '44px', flex: 'none' } }),
              h('span', { class: 'th', style: { flex: '1' }, text: t('people.colNameJob') }),
              h('span', { class: 'th', style: { width: '130px', flex: 'none' }, text: t('people.colRole') }),
              h('span', { class: 'th', style: { width: '150px', flex: 'none' }, text: t('people.colStatus') }),
              h('span', { class: 'th', style: { width: '40px', flex: 'none' } }),
            ),
            ...filtered.map(row),
          ),
      // A terv lablece a ferohelykeretet mutatja. A `maxUser` a
      // cegdokumentumon eddig is megvolt, csak sehol nem latszott: nem derult
      // ki, hany hely maradt meg.
      limit === undefined
        ? h('span')
        : h(
            'div',
            {
              class: 'row between',
              style: { padding: '12px 4px', alignItems: 'center', gap: '12px' },
            },
            h('span', {
              class: 'caption',
              text: `${options.people.length} felhasználó a ${limit} fős keretből · ${free} hely szabad`,
            }),
            h(
              'div',
              {
                class: 'prog',
                style: { width: '180px', flex: 'none' },
                role: 'progressbar',
                'aria-valuenow': String(options.people.length),
                'aria-valuemin': '0',
                'aria-valuemax': String(limit),
                'aria-label': t('people.seatUsage'),
              },
              h('i', {
                style: {
                  width: `${Math.min(100, Math.round((options.people.length / Math.max(1, limit)) * 100))}%`,
                  background: options.people.length >= limit ? 'var(--err)' : undefined,
                },
              }),
            ),
          ),
    );
  }
  renderList();

  const search = h('input', {
    class: 'input',
    type: 'search',
    placeholder: t('people.searchPlaceholder'),
    'aria-label': t('people.searchLabel'),
    style: { height: '36px' },
    onInput: (event: Event) => {
      query = (event.target as HTMLInputElement).value;
      renderList();
    },
  });

  /// A terv fülsora a három alnézet között vált, darabszámmal. Korábban
  /// gombok álltak itt: nem látszott, hogy ugyanannak a képernyőnek a
  /// nézeteiről van szó, se az, mennyi elem van bennük.
  const tabs = h(
    'div',
    { class: 'tabs', role: 'tablist', style: { border: '0' } },
    h(
      'button',
      {
        class: 'tab on',
        type: 'button',
        role: 'tab',
        'aria-selected': 'true',
      },
      h('span', { text: t('people.tabUsers') }),
      h('span', { class: 'mono tm', text: String(options.people.length) }),
    ),
    h(
      'button',
      {
        class: 'tab',
        type: 'button',
        role: 'tab',
        'aria-selected': 'false',
        onClick: options.onManageGroups,
      },
      h('span', { text: t('people.tabGroups') }),
      h('span', { class: 'mono tm', text: String(options.groups.length) }),
    ),
    h(
      'button',
      {
        class: 'tab',
        type: 'button',
        role: 'tab',
        'aria-selected': 'false',
        onClick: options.onManageJobTitles,
      },
      h('span', { text: t('people.tabJobTitles') }),
      options.jobTitleCount !== undefined
        ? h('span', { class: 'mono tm', text: String(options.jobTitleCount) })
        : null,
    ),
  );

  return h(
    'div',
    { class: 'col', style: { gap: '0' } },
    h(
      'div',
      {
        class: 'row g8',
        style: {
          padding: '12px 20px',
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          alignItems: 'center',
          flexWrap: 'wrap',
        },
      },
      tabs,
      h('span', { style: { marginLeft: 'auto', width: '300px', maxWidth: '100%' } }, search),
      h('button', {
        class: 'btn btn-primary btn-sm',
        type: 'button',
        text: t('people.newUser'),
        onClick: options.onNewUser,
      }),
    ),
    h('div', { style: { padding: '20px' } }, listHost),
  );
}

// ---------------------------------------------------------------------------
// W24 — Új / szerkesztett felhasználó
// ---------------------------------------------------------------------------

export type UserInput = {
  lastName: string;
  firstName: string;
  email: string;
  jobTitle?: string;
  role: Role;
  /// A csoportok, amelyekbe a felhasználó bekerül.
  groupIds: string[];
  /// A kiosztott hely szintje. Az AI-s hely nyitja meg az „AI felvétel"
  /// menüpontot — a cég a Stripe-ban a darabszámot veszi meg, a kiosztás
  /// viszont a cégadmin dolga.
  seatTier?: 'basic' | 'ai';
};

/// A szerepkörök rövid leírása — a terv minden választás alá kiírja, MIT
/// jelent. Enélkül a „Művezető" és a „Cégadmin" közti különbség csak
/// találgatás volt.
/// A terv három fokozatot mutat (Munkatárs / Művezető / Cégadmin). A
/// tagság `role` mezője viszont CSAK `SUPER` és `ADMIN` értéket ismer, minden
/// más munkatárs — középső fokozat nincs a modellben, ezért nem is kínáljuk.
const roleDescriptionKeys: Record<Role, string> = {
  USER: 'people.roleDescUser',
  ADMIN: 'people.roleDescAdmin',
  SUPER: 'people.roleDescSuper',
};

function roleDescription(role: Role): string {
  return t(roleDescriptionKeys[role]);
}

export function openUserDialog(options: {
  person?: PersonView;
  role?: Role;
  jobTitles: string[];
  groups?: GroupView[];
  /// A felhasználó mostani csoportjai.
  groupIds?: string[];
  /// Csak a cégadminok látják — a szuperadmin szerepkört nem osztjuk itt.
  assignableRoles?: Role[];
  /// A megvásárolt és a már kiosztott AI-s helyek száma.
  ///
  /// Csak TÁJÉKOZTAT és figyelmeztet: a kemény korlátot nem itt tartjuk be,
  /// mert a felületet meg lehet kerülni. A jelenléte viszont megakadályozza a
  /// leggyakoribb hibát — hogy az admin kiosztson egy nem létező helyet.
  aiSeats?: { purchased: number; used: number };
  onSubmit: (user: UserInput) => void;
  onDelete?: () => void;
  onSendPasswordReset?: () => void;
}): void {
  // A teljes nev magyar sorrendben all: vezeteknev + keresztnev.
  const [existingLast = '', ...restName] = (options.person?.fullName ?? '').split(' ');

  const lastName = textField({ label: t('register.lastName'), required: true, value: existingLast });
  const firstName = textField({
    label: t('register.firstName'),
    required: true,
    value: restName.join(' '),
  });
  // A cím ELŐRE KI VAN TÖLTVE meglévő felhasználónál. Enélkül a mező üresen
  // nyílt, és mivel kötelező, a szerkesztés MENTHETETLEN volt: a „Mentés"
  // némán visszapattant, mert a validáció elbukott egy olyan mezőn, amit a
  // felhasználó nem is akart módosítani.
  //
  // És mivel a mentés SZÁNDÉKOSAN nem írja a címet (az a bejelentkezési
  // azonosító is egyben, lásd `saveUserProfile`), szerkesztéskor a mező
  // CSAK OLVASHATÓ. Átírhatónak hagyni hazugság lenne: a felhasználó
  // átírná, mentene, és semmi nem történne.
  const email = textField({
    label: t('auth.email'),
    required: true,
    placeholder: t('auth.emailPlaceholder'),
    value: options.person?.email,
    hint: options.person
      ? t('people.emailNotEditable')
      : t('people.emailHintNew'),
  });
  if (options.person) {
    email.input.readOnly = true;
    email.input.setAttribute('aria-readonly', 'true');
  }
  const jobTitle = selectField({
    label: t('people.jobTitle'),
    value: options.person?.jobTitle,
    choices: [
      { value: '', label: t('people.none') },
      ...options.jobTitles.map((title) => ({ value: title, label: title })),
    ],
  });

  let selectedRole: Role = options.role ?? 'USER';
  // A hely szintje. Alapértelmezés a sima hely: AI-t SOHA nem adunk
  // magától — az pénzbe kerül.
  let selectedSeat: 'basic' | 'ai' = options.person?.seatTier === 'ai' ? 'ai' : 'basic';
  const seatSelect = selectField({
    label: t('people.seatTier'),
    value: selectedSeat,
    choices: [
      { value: 'basic', label: t('people.seatBasic') },
      { value: 'ai', label: t('people.seatAi') },
    ],
    hint: options.aiSeats
      ? t('people.seatAiHint', [
          String(options.aiSeats.used),
          String(options.aiSeats.purchased),
        ])
      : undefined,
  });
  const seatWarning = h('span', { class: 'errtx', style: { display: 'none' } });
  seatSelect.input.addEventListener('change', () => {
    selectedSeat = seatSelect.input.value === 'ai' ? 'ai' : 'basic';
    const keret = options.aiSeats;
    // Csak akkor figyelmeztetunk, ha a valtoztatas UJ AI-helyet foglalna: aki
    // mar AI-s volt, azt nem szamoljuk ujra.
    const ujFoglalas = selectedSeat === 'ai' && options.person?.seatTier !== 'ai';
    const tullepi = keret !== undefined && ujFoglalas && keret.used >= keret.purchased;
    seatWarning.textContent = tullepi ? t('people.seatAiNoFree') : '';
    seatWarning.style.display = tullepi ? '' : 'none';
  });

  const assignable = options.assignableRoles ?? (['USER', 'ADMIN'] as Role[]);
  const roleHost = h('div', { class: 'col g8' });
  const roleError = h('span', { class: 'errtx', style: { display: 'none' } });

  /// A terv kártyákat mutat a szerepköröknél, nem legördülőt: a leírás így
  /// a választás mellett áll, nem egy menüben rejtve.
  function renderRoles(): void {
    roleHost.replaceChildren(
      ...assignable.map((role) => {
        const on = role === selectedRole;
        return h(
          'label',
          {
            class: 'row g10',
            style: {
              alignItems: 'flex-start',
              padding: '10px 12px',
              borderRadius: '10px',
              border: `1px solid ${on ? 'var(--accent-bd)' : 'var(--border)'}`,
              background: on ? 'var(--accent-soft)' : 'transparent',
              cursor: 'pointer',
              position: 'relative',
            },
          },
          h('input', {
            type: 'radio',
            name: 'user-role',
            checked: on ? 'checked' : undefined,
            style: { position: 'absolute', opacity: '0', width: '20px', height: '20px', margin: '0' },
            onChange: () => {
              selectedRole = role;
              renderRoles();
            },
          }),
          h('span', { class: `rd${on ? ' on' : ''}`, 'aria-hidden': 'true', style: { marginTop: '2px' } }),
          h(
            'span',
            { class: 'col', style: { gap: '2px', minWidth: '0' } },
            h('span', { class: 'body-sm w6', text: roleLabel(role) }),
            h('span', { class: 'caption', text: roleDescription(role) }),
          ),
        );
      }),
    );
  }
  renderRoles();

  // A csoporttagsag eddig SEHOL nem volt szerkesztheto a weben, pedig a
  // csoportok a `userIds` listajukban tarolják — a terv (W24) chip-mezot
  // mutat rá.
  const groups = options.groups ?? [];
  const selectedGroups = new Set(options.groupIds ?? []);
  const groupHost = h('div');
  let groupPickerOpen = false;

  function renderGroups(): void {
    groupHost.replaceChildren(
      h(
        'div',
        {
          class: 'input row g6 wrap',
          style: { height: 'auto', minHeight: '44px', padding: '6px 10px', alignItems: 'center' },
        },
        ...[...selectedGroups].map((id) => {
          const name = groups.find((g) => g.id === id)?.name ?? id;
          return h(
            'span',
            { class: 'chip on', style: { height: '28px' } },
            h('span', { text: name }),
            h('button', {
              class: 'x',
              type: 'button',
              'aria-label': t('people.removeChip', [name]),
              text: '×',
              style: { border: '0', background: 'none', cursor: 'pointer', font: 'inherit' },
              onClick: () => {
                selectedGroups.delete(id);
                renderGroups();
              },
            }),
          );
        }),
        groups.length === 0
          ? h('span', { class: 'caption', text: t('people.noGroupsInCompany') })
          : h('button', {
              class: 'btn btn-ghost btn-sm',
              type: 'button',
              text: t('people.addMore'),
              onClick: () => {
                groupPickerOpen = !groupPickerOpen;
                renderGroups();
              },
            }),
      ),
      !groupPickerOpen
        ? h('span')
        : h(
            'div',
            {
              class: 'col g4',
              style: {
                marginTop: '6px',
                maxHeight: '180px',
                overflow: 'auto',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                padding: '6px',
              },
            },
            ...groups.map((group) => {
              const on = selectedGroups.has(group.id);
              return h(
                'label',
                {
                  class: 'row g12',
                  style: {
                    padding: '6px 8px',
                    borderRadius: 'var(--r-sm)',
                    cursor: 'pointer',
                    background: on ? 'var(--accent-soft)' : 'transparent',
                  },
                },
                h('input', {
                  type: 'checkbox',
                  checked: on ? 'checked' : undefined,
                  onChange: () => {
                    if (on) selectedGroups.delete(group.id);
                    else selectedGroups.add(group.id);
                    renderGroups();
                  },
                }),
                h('span', { class: 'body-sm', text: group.name }),
              );
            }),
          ),
    );
  }
  renderGroups();

  openDialog({
    title: options.person ? t('people.editUser') : t('people.newUser'),
    // A terv 1000 px-es, ketoszlopos urlapot mutat.
    width: '1000px',
    body: () =>
      h(
        'div',
        { class: 'row', style: { gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' } },
        h(
          'div',
          { class: 'col g16', style: { flex: '1 1 0', minWidth: '320px' } },
          h('span', { class: 'h3', text: t('people.basics') }),
          h(
            'div',
            { class: 'grid c2', style: { gap: '16px 20px' } },
            lastName.element,
            firstName.element,
            email.element,
            jobTitle.element,
          ),
          h(
            'div',
            { class: 'field' },
            h(
              'span',
              { class: 'flbl' },
              h('span', { text: t('people.colRole') }),
              h('span', { class: 'req', text: ' *' }),
            ),
            roleHost,
            roleError,
          ),
          h('div', { class: 'col g4' }, seatSelect.element, seatWarning),
          h(
            'div',
            { class: 'field' },
            h('span', { class: 'flbl', text: t('people.tabGroups') }),
            groupHost,
          ),
        ),
        h(
          'div',
          { class: 'col g14', style: { width: '280px', flex: 'none', gap: '14px' } },
          h(
            'div',
            { class: 'card pad20 col', style: { alignItems: 'center', gap: '12px' } },
            avatar(options.person?.fullName ?? t('people.newUser'), 'lg'),
            // A terv kepfeltoltest kinal. A felhasznalo-dokumentum `photoUrl`
            // mezoje letezik, de a webrol feltoltes nincs bekotve — kitalalt
            // gombot nem teszunk ki.
            h('span', {
              class: 'caption',
              style: { textAlign: 'center' },
              text: t('people.photoHintDialog'),
            }),
          ),
          options.person
            ? h(
                'div',
                { class: 'card pad16 col', style: { gap: '8px' } },
                h('span', { class: 'label', text: t('people.account') }),
                options.onSendPasswordReset
                  ? h('button', {
                      class: 'btn btn-secondary btn-sm',
                      type: 'button',
                      text: t('people.sendPasswordReset'),
                      onClick: options.onSendPasswordReset,
                    })
                  : null,
                options.onDelete
                  ? h('button', {
                      class: 'btn btn-ghost btn-sm',
                      type: 'button',
                      style: { color: 'var(--err)' },
                      text: t('people.deleteUser'),
                      onClick: () => {
                        // Visszafordithatatlan muvelet: megerosites nelkul nem megy.
                        confirmDialog({
                          title: t('people.deleteUser'),
                          message:
                            t('people.deleteUserMessage'),
                          confirmLabel: t('general.delete'),
                          danger: true,
                          onConfirm: () => options.onDelete?.(),
                        });
                      },
                    })
                  : null,
              )
            : null,
        ),
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          if (!validateAll([lastName, firstName, email] as FieldControl<HTMLElement>[])) return;
          const address = email.input.value.trim();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
            email.setError(t('auth.invalidEmail'));
            email.input.focus();
            return;
          }
          options.onSubmit({
            lastName: lastName.input.value.trim(),
            firstName: firstName.input.value.trim(),
            email: address,
            jobTitle: jobTitle.input.value || undefined,
            role: selectedRole,
            groupIds: [...selectedGroups],
            seatTier: selectedSeat,
          });
          close();
        },
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// W25 — Saját profil
// ---------------------------------------------------------------------------

/// A profilon állítható értesítési beállítások.
///
/// A mezők PONTOSAN azok, amiket a közös adatmodell tárol
/// (`users/{uid}.autoUserPush`). A terv (W25) öt kapcsolót mutat, de a
/// többihez nincs mögöttes mező — kitalált beállítást pedig nem teszünk ki,
/// mert az hazudna: a felhasználó átkapcsolná, és semmi nem történne.
export type NotificationSettings = {
  beforeDeadlineDays: number;
  beforeDeadlineHours: number;
  beforeDeadlineMinutes: number;
  afterDeadlineForAssigner: boolean;
};

export function profilePage(options: {
  person: {
    fullName: string;
    email: string;
    jobTitle?: string;
    company?: string;
    role?: Role;
    photoUrl?: string;
  };
  /// Profilkép választása. Elhagyható: ahol a feltöltés nincs bekötve, ott a
  /// gomb sem jelenik meg — működésképtelen gombot nem teszünk ki.
  onPickPhoto?: (file: File) => void;
  settings: NotificationSettings;
  /// A felület témája. A beállítás a fejlécben is ott van; a terv szerint
  /// a profilon is állítható.
  theme?: 'system' | 'light' | 'dark';
  onThemeChange?: (theme: 'system' | 'light' | 'dark') => void;
  onSaveSettings: (settings: NotificationSettings) => void;
  onChangePassword: () => void;
  onSignOut: () => void;
  /// A push-értesítés kapcsolója (4.3). Elhagyható: ahol nincs bekötve
  /// (például a fejlesztői előnézetben), ott a kártya sem jelenik meg —
  /// működésképtelen kapcsolót nem teszünk ki.
  push?: {
    /// Be van-e kapcsolva EZEN a böngészőn.
    enabled: boolean;
    /// Miért nem állítható, ha nem állítható.
    blocked?: 'denied' | 'unsupported' | 'missing-key';
    onChange: (on: boolean) => void;
  };
}): HTMLElement {
  const settings = { ...options.settings };

  /// Címke/érték sor — ugyanaz a minta, mint a feladat és a projekt
  /// részleteinél.
  function row(label: string, value: HTMLElement | string): HTMLElement {
    return h(
      'div',
      {
        class: 'row g12',
        style: { alignItems: 'baseline', padding: '7px 0', borderBottom: '1px solid var(--border)' },
      },
      h('span', { class: 'caption', style: { width: '140px', flex: 'none' }, text: label }),
      typeof value === 'string'
        ? h('span', {
            class: 'body-sm',
            style: { flexGrow: '1', minWidth: '0', overflowWrap: 'anywhere' },
            text: value,
          })
        : h('span', { style: { flexGrow: '1', minWidth: '0' } }, value),
    );
  }

  /// Kapcsolósor a terv szerint: cím + magyarázat balra, `.sw` jobbra.
  function switchRow(
    title: string,
    caption: string,
    value: boolean,
    apply: (v: boolean) => void,
  ): HTMLElement {
    const box = h('span', { class: `sw${value ? ' on' : ''}`, 'aria-hidden': 'true' });
    const input = h('input', {
      type: 'checkbox',
      checked: value ? 'checked' : undefined,
      style: { position: 'absolute', opacity: '0', width: '46px', height: '26px', margin: '0' },
      onChange: (event: Event) => {
        const on = (event.target as HTMLInputElement).checked;
        box.className = `sw${on ? ' on' : ''}`;
        apply(on);
      },
    });

    return h(
      'label',
      {
        class: 'row g12',
        style: {
          alignItems: 'center',
          padding: '10px 0',
          borderBottom: '1px solid var(--border)',
          cursor: 'pointer',
          position: 'relative',
        },
      },
      h(
        'span',
        { class: 'col grow', style: { gap: '0', minWidth: '0' } },
        h('span', { class: 'body-sm w6', text: title }),
        h('span', { class: 'caption', text: caption }),
      ),
      input,
      box,
    );
  }

  const leadTime = h('div', { class: 'row g8 wrap', style: { alignItems: 'center' } });
  function numberBox(label: string, value: number, apply: (n: number) => void): HTMLElement {
    return h(
      'div',
      { class: 'col', style: { gap: '4px' } },
      h('input', {
        class: 'input mono',
        type: 'number',
        min: '0',
        value: String(value),
        style: { width: '72px', textAlign: 'center' },
        'aria-label': label,
        onChange: (event: Event) => apply(Number((event.target as HTMLInputElement).value)),
      }),
      h('span', { class: 'caption', style: { textAlign: 'center' }, text: label }),
    );
  }
  leadTime.replaceChildren(
    numberBox(t('people.unitDay'), settings.beforeDeadlineDays, (n) => {
      settings.beforeDeadlineDays = n;
    }),
    numberBox(t('people.unitHour'), settings.beforeDeadlineHours, (n) => {
      settings.beforeDeadlineHours = n;
    }),
    numberBox(t('people.unitMinute'), settings.beforeDeadlineMinutes, (n) => {
      settings.beforeDeadlineMinutes = n;
    }),
  );

  const themes: { key: 'light' | 'dark' | 'system'; label: string }[] = [
    { key: 'light', label: t('people.themeLight') },
    { key: 'dark', label: t('people.themeDark') },
    { key: 'system', label: t('people.themeSystem') },
  ];

  return h(
    'div',
    {
      class: 'row',
      style: {
        gap: '20px',
        padding: '24px',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        maxWidth: '1000px',
      },
    },
    h(
      'div',
      { style: { width: '300px', flex: 'none' } },
      h(
        'div',
        { class: 'card pad20 col', style: { alignItems: 'center', gap: '12px' } },
        avatar(options.person.fullName, 'xl', options.person.photoUrl),
        h('span', { class: 'body w6', text: options.person.fullName }),
        // A terv képválasztót kínál. A `photoUrl` mező a közös modellben
        // eddig is ott volt (a mobil írja is) — csak a webről feltölteni nem
        // lehetett. Most lehet; a gomb elhagyható, hogy a képernyő ott is
        // működjön, ahol a feltöltés nincs bekötve.
        ...(options.onPickPhoto
          ? [
              (() => {
                // Rejtett fájlválasztó: a `.btn` megjelenését nem lehet
                // rávenni egy natív `<input type="file">`-ra, a ds.css-ben
                // pedig nincs erre osztály. A gomb nyitja meg.
                const picker = h('input', {
                  type: 'file',
                  accept: 'image/*',
                  style: { display: 'none' },
                  onChange: (event: Event) => {
                    const input = event.target as HTMLInputElement;
                    const file = input.files?.[0];
                    if (file) options.onPickPhoto?.(file);
                    // Ugyanazt a fájlt kétszer is ki lehessen választani.
                    input.value = '';
                  },
                });
                return h(
                  'span',
                  { class: 'col', style: { gap: '6px', alignItems: 'center' } },
                  picker,
                  h('button', {
                    class: 'btn btn-secondary btn-sm',
                    type: 'button',
                    text: t('people.choosePhoto'),
                    onClick: () => picker.click(),
                  }),
                  h('span', { class: 'caption', text: t('people.photoHint') }),
                );
              })(),
            ]
          : [
              h('span', {
                class: 'caption',
                style: { textAlign: 'center' },
                text: t('people.photoHintProfile'),
              }),
            ]),
        h('button', {
          class: 'btn btn-secondary btn-sm',
          type: 'button',
          text: t('user.logout'),
          onClick: options.onSignOut,
        }),
      ),
    ),
    h(
      'div',
      { class: 'col g14', style: { flex: '1 1 0', minWidth: '320px', gap: '14px' } },
      h(
        'div',
        { class: 'card pad16 col', style: { gap: '2px' } },
        row(t('people.rowName'), options.person.fullName),
        row(t('people.rowEmail'), options.person.email),
        options.person.jobTitle ? row(t('people.jobTitle'), options.person.jobTitle) : null,
        options.person.role ? row(t('people.colRole'), roleLabel(options.person.role)) : null,
        options.person.company ? row(t('people.rowCompany'), options.person.company) : null,
      ),
      options.onThemeChange
        ? h(
            'div',
            { class: 'card pad16 col', style: { gap: '12px' } },
            h('span', { class: 'label', text: t('people.appearance') }),
            h(
              'div',
              { class: 'row g12', style: { alignItems: 'center' } },
              h('span', { class: 'body-sm grow', text: t('people.theme') }),
              h(
                'div',
                { class: 'seg' },
                ...themes.map((option) =>
                  h('button', {
                    type: 'button',
                    class: options.theme === option.key ? 'on' : '',
                    'aria-pressed': options.theme === option.key ? 'true' : 'false',
                    text: option.label,
                    onClick: () => options.onThemeChange?.(option.key),
                  }),
                ),
              ),
            ),
          )
        : null,
      h(
        'div',
        { class: 'card pad16 col', style: { gap: '10px' } },
        h('span', { class: 'label', text: t('noti.title') }),
        h('span', {
          class: 'caption',
          text: t('people.leadTimeHint'),
        }),
        leadTime,
        switchRow(
          t('people.notifyOverdueTitle'),
          t('people.notifyOverdueText'),
          settings.afterDeadlineForAssigner,
          (v) => {
            settings.afterDeadlineForAssigner = v;
          },
        ),
        // A terv itt „Megjegyzés vagy megjelölés" kapcsolót mutat, de a közös
        // adatmodellben NINCS ilyen felhasználó-szintű mező — a mobilon ez
        // feladatonként állítható. Kapcsoló helyett ezért ezt mondjuk meg.
        h('span', { class: 'caption', text: t('people.commentNotifyNote') }),
        h(
          'div',
          { class: 'row', style: { paddingTop: '6px' } },
          h('button', {
            class: 'btn btn-primary',
            type: 'button',
            text: t('people.saveSettings'),
            onClick: () => options.onSaveSettings(settings),
          }),
        ),
      ),
      // 4.3 — a push kapcsolója a saját kártyáján, az értesítési szabályok
      // ALATT: azok azt mondják meg, MIRŐL szóljunk, ez pedig azt, HOGYAN.
      options.push
        ? h(
            'div',
            { class: 'card pad16 col', style: { gap: '10px' } },
            h('span', { class: 'label', text: t('push.title') }),
            options.push.blocked
              ? h('span', {
                  class: 'body-sm',
                  style: { color: 'var(--warn)' },
                  text:
                    options.push.blocked === 'denied'
                      ? t('push.denied')
                      : options.push.blocked === 'unsupported'
                        ? t('push.unsupported')
                        : t('push.missingKey'),
                })
              : switchRow(
                  t('push.switchTitle'),
                  t('push.switchCaption'),
                  options.push.enabled,
                  (value) => options.push?.onChange(value),
                ),
            h('span', { class: 'caption', text: t('push.perDevice') }),
          )
        : null,
      h(
        'div',
        { class: 'card pad16 col', style: { gap: '10px' } },
        h('span', { class: 'label', text: t('people.security') }),
        h('button', {
          class: 'btn btn-secondary',
          type: 'button',
          style: { alignSelf: 'flex-start' },
          text: t('people.changePassword'),
          onClick: options.onChangePassword,
        }),
        h('span', {
          class: 'caption',
          text: t('people.changePasswordHint'),
        }),
      ),
    ),
  );
}

// ---------------------------------------------------------------------------
// W26 — Csoportok
// ---------------------------------------------------------------------------

export function openGroupDialog(options: {
  group?: GroupView;
  people: PersonView[];
  onSubmit: (group: { name: string; userIds: string[] }) => void;
  onDelete?: () => void;
}): void {
  const name = textField({ label: 'Csoport neve', required: true, value: options.group?.name });
  const selected = new Set(options.group?.userIds ?? []);
  const listHost = h('div', { class: 'col g4', style: { maxHeight: '260px', overflow: 'auto' } });
  const error = h('span', { class: 'errtx', style: { display: 'none' } });

  function renderMembers(): void {
    listHost.replaceChildren(
      ...options.people.map((person) => {
        const on = selected.has(person.id);
        return h(
          'label',
          {
            class: 'row g12',
            style: {
              padding: '8px 10px',
              borderRadius: 'var(--r-sm)',
              cursor: 'pointer',
              background: on ? 'var(--accent-soft)' : 'transparent',
            },
          },
          h('input', {
            type: 'checkbox',
            checked: on ? 'checked' : undefined,
            onChange: () => {
              on ? selected.delete(person.id) : selected.add(person.id);
              error.style.display = 'none';
              renderMembers();
            },
          }),
          avatar(person.fullName, 'sm', person.photoUrl),
          h('span', { class: 'body-sm grow', text: person.fullName }),
        );
      }),
    );
  }
  renderMembers();

  openDialog({
    title: options.group ? t('people.editGroup') : t('people.newGroup'),
    width: '560px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        name.element,
        h('span', { class: 'flbl', text: t('people.groupMembers') }),
        listHost,
        error,
      ),
    footer: (close) => [
      options.onDelete
        ? h('button', {
            class: 'btn btn-danger',
            type: 'button',
            text: t('general.delete'),
            onClick: () => {
              options.onDelete?.();
              close();
            },
          })
        : h('span'),
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          if (!name.validate()) return;
          if (selected.size === 0) {
            // Ures csoportra nem lehet feladatot kiosztani.
            error.textContent = t('people.pickAtLeastOne');
            error.style.display = '';
            return;
          }
          options.onSubmit({ name: name.input.value.trim(), userIds: [...selected] });
          close();
        },
      }),
    ],
  });
}

/// A csoport alcíme: a tagok munkakörei. A csoportdokumentum leírást NEM
/// tárol (`name` + `userIds`), ezért az áll itt, ami tudható — kik vannak
/// benne, milyen munkakörrel.
function groupCaption(group: GroupView, people: PersonView[]): string {
  const titles = [
    ...new Set(
      group.userIds
        .map((id) => people.find((p) => p.id === id)?.jobTitle)
        .filter((title): title is string => Boolean(title)),
    ),
  ];
  if (titles.length === 0) return t('people.mixedGroup');
  return titles.slice(0, 3).join(', ') + (titles.length > 3 ? '…' : '');
}

export function groupsPage(options: {
  groups: GroupView[];
  people: PersonView[];
  onNew: () => void;
  onEdit: (group: GroupView) => void;
  onSave?: (group: GroupView, next: { name: string; userIds: string[] }) => void;
  onAddMembers?: (group: GroupView) => void;
}): HTMLElement {
  let selected: GroupView | undefined = options.groups[0];

  const listHost = h('div', { class: 'panel', style: { overflow: 'hidden' } });
  const editorHost = h('div', {
    class: 'panel pad24 col g18',
    style: { flex: '1 1 0', minWidth: '320px', gap: '18px' },
  });

  function renderList(): void {
    listHost.replaceChildren(
      ...(options.groups.length === 0
        ? [
            h('span', {
              class: 'caption',
              style: { padding: '14px', display: 'block' },
              text: t('people.noGroupYet'),
            }),
          ]
        : options.groups.map((group) =>
            h(
              'button',
              {
                type: 'button',
                class: `lrow${group.id === selected?.id ? ' on' : ''}`,
                style: {
                  width: '100%',
                  border: '0',
                  borderBottom: '1px solid var(--border)',
                  font: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'left',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                },
                onClick: () => {
                  selected = group;
                  render();
                },
              },
              h(
                'span',
                {
                  class: 'ic',
                  style: {
                    width: '34px',
                    height: '34px',
                    borderRadius: '9px',
                    background: 'var(--sunken)',
                    color: 'var(--ts)',
                    flex: 'none',
                  },
                },
                icon(icons.users, 17),
              ),
              h(
                'span',
                { class: 'col grow', style: { gap: '0', minWidth: '0', alignItems: 'flex-start' } },
                h('span', { class: 'body-sm w6', text: group.name }),
                h('span', { class: 'caption', text: groupCaption(group, options.people) }),
              ),
              h(
                'span',
                { class: 'chip', style: { height: '26px', flex: 'none' } },
                h('span', { class: 'mono', text: String(group.userIds.length) }),
                h('span', { text: t('people.unitPeople') }),
              ),
            ),
          )),
    );
  }

  function renderEditor(): void {
    if (!selected) {
      editorHost.replaceChildren(
        emptyState({
          title: t('people.noGroupsTitle'),
          text: t('people.noGroupsText'),
          actionLabel: t('people.newGroup'),
          onAction: options.onNew,
        }),
      );
      return;
    }

    const group = selected;
    const members = group.userIds
      .map((id) => options.people.find((p) => p.id === id))
      .filter((p): p is PersonView => p !== undefined);

    editorHost.replaceChildren(
      h(
        'div',
        { class: 'col', style: { gap: '2px' } },
        h('span', { class: 'h3', text: group.name }),
        h('span', { class: 'caption', text: `${group.userIds.length} tag` }),
      ),
      h(
        'div',
        { class: 'field' },
        h(
          'span',
          { class: 'flbl' },
          h('span', { text: 'Tagok' }),
          h('span', { class: 'caption', text: ` (${members.length})` }),
        ),
        h(
          'div',
          { class: 'panel', style: { overflow: 'hidden' } },
          ...(members.length === 0
            ? [
                h('span', {
                  class: 'caption',
                  style: { padding: '12px', display: 'block' },
                  text: t('people.noMembers'),
                }),
              ]
            : members.map((person) =>
                h(
                  'div',
                  {
                    class: 'row g10',
                    style: {
                      alignItems: 'center',
                      padding: '9px 12px',
                      borderBottom: '1px solid var(--border)',
                    },
                  },
                  avatar(person.fullName, 'sm', person.photoUrl),
                  h(
                    'span',
                    { class: 'col grow', style: { gap: '0', minWidth: '0' } },
                    h('span', { class: 'body-sm w6', text: person.fullName }),
                    h('span', { class: 'caption', text: person.jobTitle ?? t('people.noJobTitle') }),
                  ),
                  options.onSave
                    ? h('button', {
                        class: 'btn btn-ghost btn-icon btn-sm',
                        type: 'button',
                        'aria-label': t('people.removeFromGroup', [person.fullName]),
                        text: '✕',
                        onClick: () =>
                          options.onSave?.(group, {
                            name: group.name,
                            userIds: group.userIds.filter((id) => id !== person.id),
                          }),
                      })
                    : null,
                ),
              )),
        ),
      ),
      h(
        'div',
        { class: 'row g8 wrap' },
        h('button', {
          class: 'btn btn-secondary',
          type: 'button',
          text: t('people.editGroup'),
          onClick: () => options.onEdit(group),
        }),
        options.onAddMembers
          ? h('button', {
              class: 'btn btn-secondary',
              type: 'button',
              text: t('people.addMember'),
              onClick: () => options.onAddMembers?.(group),
            })
          : null,
      ),
    );
  }

  function render(): void {
    renderList();
    renderEditor();
  }
  render();

  return h(
    'div',
    { class: 'col', style: { gap: '0' } },
    h(
      'div',
      {
        class: 'row between',
        style: { padding: '12px 20px', borderBottom: '1px solid var(--border)' },
      },
      h('h2', { class: 'h3', text: t('people.userGroups') }),
      h('button', {
        class: 'btn btn-primary btn-sm',
        type: 'button',
        text: t('people.newGroup'),
        onClick: options.onNew,
      }),
    ),
    // A terv ketoszlopos: balra a csoportok listaja, jobbra a kivalasztott
    // csoport szerkesztoje. Eddig kartyaracs volt, es a tagokat csak egy
    // parbeszedablakban lehetett megnezni.
    h(
      'div',
      {
        class: 'row',
        style: { gap: '20px', padding: '20px', alignItems: 'flex-start', flexWrap: 'wrap' },
      },
      h('div', { style: { width: '420px', maxWidth: '100%', flex: 'none' } }, listHost),
      editorHost,
    ),
  );
}

// ---------------------------------------------------------------------------
// W27 — Munkakörök
// ---------------------------------------------------------------------------

export type JobTitleView = { id: string; name: string };

export function jobTitlesPage(options: {
  jobTitles: JobTitleView[];
  /// Hány emberhez tartozik az adott munkakör. A terv „N fő" chipje ebből
  /// él; eddig sehol nem látszott, kihez tartozik egyáltalán a munkakör.
  people?: PersonView[];
  onAdd: (title: string) => void;
  onRemove: (jobTitle: JobTitleView) => void;
  onRename?: (jobTitle: JobTitleView, name: string) => void;
}): HTMLElement {
  const input = textField({ label: t('people.newJobTitleName'), placeholder: t('people.jobTitlePlaceholder') });

  function headcount(name: string): number {
    return (options.people ?? []).filter((p) => p.jobTitle === name).length;
  }

  return h(
    'div',
    {
      class: 'col g16',
      style: { padding: '24px', maxWidth: '820px', marginLeft: 'auto', marginRight: 'auto' },
    },
    h(
      'div',
      { class: 'col', style: { gap: '2px' } },
      h('h2', { class: 'h3', text: t('people.tabJobTitles') }),
      h('span', {
        class: 'caption',
        text: t('people.jobTitlesHint'),
      }),
    ),
    options.jobTitles.length === 0
      ? h('p', { class: 'body tm', text: t('people.noJobTitleYet') })
      : h(
          'div',
          { class: 'panel', style: { overflow: 'hidden' } },
          ...options.jobTitles.map((jobTitle) => {
            const count = headcount(jobTitle.name);
            return h(
              'div',
              { class: 'lrow', style: { alignItems: 'center', flexWrap: 'nowrap' } },
              h(
                'span',
                { class: 'col grow', style: { gap: '0', minWidth: '0' } },
                h('span', { class: 'body-sm w6', text: jobTitle.name }),
                h('span', {
                  class: 'caption',
                  text:
                    count === 0
                      ? t('people.notAssignedYet')
                      : t('people.headcount', [String(count)]),
                }),
              ),
              h(
                'span',
                { class: 'chip', style: { height: '26px', flex: 'none' } },
                h('span', { class: 'mono', text: String(count) }),
                h('span', { text: t('people.unitPeople') }),
              ),
              options.onRename
                ? h(
                    'button',
                    {
                      class: 'btn btn-ghost btn-icon btn-sm',
                      type: 'button',
                      'aria-label': t('people.renameJobTitle', [jobTitle.name]),
                      onClick: () => {
                        openRenameDialog(jobTitle.name, (next) =>
                          options.onRename?.(jobTitle, next),
                        );
                      },
                    },
                    h('span', { class: 'ic' }, icon(icons.pencil)),
                  )
                : null,
              h(
                'button',
                {
                  class: 'btn btn-ghost btn-icon btn-sm',
                  type: 'button',
                  'aria-label': t('people.deleteJobTitleAria', [jobTitle.name]),
                  onClick: () => {
                    // A munkakor torlese a hozza rendelt embereket is erinti,
                    // ezert megerositest ker.
                    confirmDialog({
                      title: t('people.deleteJobTitle'),
                      message:
                        count > 0
                          ? t('people.deleteJobTitleWith', [String(count)])
                          : t('people.deleteJobTitleEmpty'),
                      confirmLabel: t('general.delete'),
                      danger: true,
                      onConfirm: () => options.onRemove(jobTitle),
                    });
                  },
                },
                h('span', { class: 'ic' }, icon(icons.trash)),
              ),
            );
          }),
        ),
    // A terv a felvevo urlapot kartyan, a lista ALATT mutatja.
    h(
      'div',
      { class: 'card pad16 row g12', style: { alignItems: 'flex-end', flexWrap: 'wrap' } },
      h('span', { style: { flexGrow: '1', minWidth: '200px' } }, input.element),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.add'),
        onClick: () => {
          const value = input.input.value.trim();
          if (value.length === 0) {
            input.setError(t('people.needJobTitle'));
            return;
          }
          if (options.jobTitles.some((j) => j.name === value)) {
            input.setError(t('people.jobTitleExists'));
            return;
          }
          options.onAdd(value);
          input.input.value = '';
          input.setError(null);
        },
      }),
    ),
  );
}

function openRenameDialog(current: string, onSubmit: (name: string) => void): void {
  const field = textField({ label: t('people.jobTitleName'), required: true, value: current });
  openDialog({
    title: t('people.renameJobTitleTitle'),
    width: '440px',
    body: () => field.element,
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          if (!field.validate()) return;
          onSubmit(field.input.value.trim());
          close();
        },
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// W28 — Felhasználóválasztó
// ---------------------------------------------------------------------------

/// A tervek közös emberválasztója.
///
/// Kereshető név, munkakör és csoport szerint; a jobb szélen a NYITOTT
/// feladatok száma — így a kiosztó látja, kire mennyi jut már. Ez a terhelés
/// eddig sehol nem jelent meg a weben.
export function openPersonPicker(options: {
  title?: string;
  people: PersonView[];
  groups?: GroupView[];
  /// Nyitott feladatok száma emberenként.
  openCounts?: Record<string, number>;
  selected?: string[];
  /// Egyetlen embert enged (pl. követő).
  single?: boolean;
  onSubmit: (ids: string[]) => void;
}): void {
  const selected = new Set(options.selected ?? []);
  const groups = options.groups ?? [];
  let query = '';
  let tab: 'people' | 'groups' = 'people';

  const body = h('div', { class: 'col g12' });
  const footerCount = h('span', { class: 'caption' });
  const submitButton = h('button', {
    class: 'btn btn-primary',
    type: 'button',
    text: t('general.add'),
    onClick: () => {
      options.onSubmit([...selected]);
      handle.close();
    },
  });

  function nameOf(id: string): string {
    return options.people.find((p) => p.id === id)?.fullName ?? id;
  }

  function personRow(person: PersonView): HTMLElement {
    const on = selected.has(person.id);
    const open = options.openCounts?.[person.id];

    return h(
      'label',
      {
        class: 'row g10',
        style: {
          alignItems: 'center',
          padding: '9px 10px',
          borderRadius: '8px',
          cursor: 'pointer',
          background: on ? 'var(--accent-soft)' : 'transparent',
          position: 'relative',
        },
      },
      h('input', {
        type: options.single ? 'radio' : 'checkbox',
        name: options.single ? 'person-pick' : undefined,
        checked: on ? 'checked' : undefined,
        style: { position: 'absolute', opacity: '0', width: '20px', height: '20px', margin: '0' },
        onChange: () => {
          if (options.single) {
            selected.clear();
            selected.add(person.id);
          } else if (on) {
            selected.delete(person.id);
          } else {
            selected.add(person.id);
          }
          render();
        },
      }),
      h(
        'span',
        { class: `cb${on ? ' on' : ''}`, 'aria-hidden': 'true' },
        on ? h('span', { class: 'ic', style: { width: '13px', height: '13px' } }, icon(icons.check, 13)) : null,
      ),
      avatar(person.fullName, 'sm', person.photoUrl),
      h(
        'span',
        { class: 'col grow', style: { gap: '0', minWidth: '0' } },
        h('span', { class: 'body-sm w6', text: person.fullName }),
        h('span', { class: 'caption', text: person.jobTitle ?? t('people.noJobTitle') }),
      ),
      open === undefined
        ? null
        : h('span', { class: 'caption', style: { flex: 'none' }, text: `${open} nyitott` }),
    );
  }

  function render(): void {
    const text = normalize(query);
    const matchingGroups = groups.filter((g) => normalize(g.name).includes(text));
    const matchingPeople = searchPeople(options.people, query).filter((person) => {
      if (text.length === 0) return true;
      // A terv szerint csoport szerint is lehet keresni.
      const inGroup = groups.some(
        (g) => normalize(g.name).includes(text) && g.userIds.includes(person.id),
      );
      return inGroup || normalize(person.fullName).includes(text) ||
        normalize(person.jobTitle ?? '').includes(text);
    });

    body.replaceChildren(
      h(
        'div',
        { class: 'input row g8', style: { alignItems: 'center' } },
        h('span', { class: 'ic', style: { width: '15px', height: '15px' } }, icon(icons.search, 15)),
        h('input', {
          type: 'search',
          value: query,
          placeholder: t('people.pickerSearchPlaceholder'),
          'aria-label': t('people.searchAria'),
          style: {
            flexGrow: '1',
            border: '0',
            background: 'none',
            font: 'inherit',
            color: 'inherit',
            outline: 'none',
          },
          onInput: (event: Event) => {
            query = (event.target as HTMLInputElement).value;
            render();
          },
        }),
      ),
      groups.length === 0
        ? h('span')
        : h(
            'div',
            { class: 'tabs', role: 'tablist' },
            h(
              'button',
              {
                class: `tab${tab === 'people' ? ' on' : ''}`,
                type: 'button',
                role: 'tab',
                'aria-selected': tab === 'people' ? 'true' : 'false',
                onClick: () => {
                  tab = 'people';
                  render();
                },
              },
              h('span', { text: t('people.tabPeople') }),
              h('span', { class: 'mono tm', text: String(matchingPeople.length) }),
            ),
            h(
              'button',
              {
                class: `tab${tab === 'groups' ? ' on' : ''}`,
                type: 'button',
                role: 'tab',
                'aria-selected': tab === 'groups' ? 'true' : 'false',
                onClick: () => {
                  tab = 'groups';
                  render();
                },
              },
              h('span', { text: t('people.tabGroups') }),
              h('span', { class: 'mono tm', text: String(matchingGroups.length) }),
            ),
          ),
      selected.size === 0
        ? h('span')
        : h(
            'div',
            { class: 'row g6 wrap' },
            ...[...selected].map((id) =>
              h(
                'span',
                { class: 'chip on' },
                h('span', { text: nameOf(id) }),
                h('button', {
                  class: 'x',
                  type: 'button',
                  'aria-label': `${nameOf(id)} eltávolítása`,
                  text: '×',
                  style: { border: '0', background: 'none', cursor: 'pointer', font: 'inherit' },
                  onClick: () => {
                    selected.delete(id);
                    render();
                  },
                }),
              ),
            ),
          ),
      h(
        'div',
        { class: 'col', style: { gap: '2px', maxHeight: '300px', overflow: 'auto' } },
        ...(tab === 'people'
          ? matchingPeople.length === 0
            ? [h('span', { class: 'caption', text: t('people.noResult') })]
            : matchingPeople.map(personRow)
          : matchingGroups.length === 0
            ? [h('span', { class: 'caption', text: t('people.noResult') })]
            : matchingGroups.map((group) =>
                h(
                  'button',
                  {
                    type: 'button',
                    class: 'row g10',
                    style: {
                      alignItems: 'center',
                      padding: '9px 10px',
                      borderRadius: '8px',
                      border: '0',
                      background: 'none',
                      font: 'inherit',
                      color: 'inherit',
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                    },
                    onClick: () => {
                      // A csoport valasztasa a TAGJAIT adja hozza: a modell
                      // feladatonkent embereket tarol, nem csoportot.
                      for (const id of group.userIds) selected.add(id);
                      tab = 'people';
                      render();
                    },
                  },
                  h(
                    'span',
                    {
                      class: 'ic',
                      style: {
                        width: '34px',
                        height: '34px',
                        borderRadius: '9px',
                        background: 'var(--sunken)',
                        color: 'var(--ts)',
                        flex: 'none',
                      },
                    },
                    icon(icons.users, 17),
                  ),
                  h(
                    'span',
                    { class: 'col grow', style: { gap: '0', minWidth: '0', alignItems: 'flex-start' } },
                    h('span', { class: 'body-sm w6', text: group.name }),
                    h('span', { class: 'caption', text: groupCaption(group, options.people) }),
                  ),
                  h('span', { class: 'caption', text: `${group.userIds.length} fő` }),
                ),
              )),
      ),
    );

    footerCount.textContent = `${selected.size} kiválasztva`;
    submitButton.textContent =
      selected.size === 0
        ? t('general.add')
        : t('people.addCount', [String(selected.size)]);
  }

  render();

  const handle = openDialog({
    title: options.title ?? t('people.pickerTitle'),
    width: '520px',
    body: () =>
      h(
        'div',
        { class: 'col g12' },
        h('span', {
          class: 'caption',
          text:
            t('people.pickerHint'),
        }),
        body,
      ),
    footer: (close) => [
      footerCount,
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      submitButton,
    ],
  });
}
